/**
 * Urkalkulation Export — lädt das Urkalkulations-PDF (vollständige
 * Offenlegung der Kalkulation je LV-Position) vom Server herunter.
 *
 * Sekundärer Export-Button im Stil der übrigen Export-Komponenten
 * (docx-export.tsx / GAEBExport.tsx) für die Angebots-Sidebar. Seit Welle 1.3
 * der UX-Entlastung wahlweise als DropdownMenuItem (asMenuItem) im
 * "Export ▾"-Menü der Sidebar rendert.
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Calculator, Check, Loader2 } from 'lucide-react';
import { useOfferStore } from '@/lib/store';
import { useToast } from '@/hooks/use-toast';

interface UrkalkulationExportProps {
  /** Als DropdownMenuItem rendern statt als eigenständiger Button (Export ▾-Menü). */
  asMenuItem?: boolean;
  /** Externer In-Flight-Guard aus dem persistenten Parent — siehe blanko-lv-export.tsx. */
  busy?: boolean;
  onRunningChange?: (running: boolean) => void;
}

export default function UrkalkulationExport({ asMenuItem = false, busy = false, onRunningChange }: UrkalkulationExportProps = {}) {
  const { toast } = useToast();
  const [isExporting, setIsExporting] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);

  const quoteHeader = useOfferStore((state) => state.quoteHeader);
  const quoteItems = useOfferStore((state) => state.quoteItems);
  const gewerkOrder = useOfferStore((state) => state.gewerkOrder);
  const kalkulationsDefaults = useOfferStore((state) => state.kalkulationsDefaults);

  const handleExport = async () => {
    if (quoteItems.length === 0) return;
    // Doppelklick-/Doppelrequest-Schutz (lokal ODER laut Parent-Guard).
    if (busy || isExporting) return;

    onRunningChange?.(true);
    setIsExporting(true);
    setExportSuccess(false);

    try {
      const response = await fetch('/api/urkalkulation/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          header: quoteHeader,
          items: quoteItems,
          gewerkOrder,
          defaults: kalkulationsDefaults,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `Server-Fehler ${response.status}` }));
        throw new Error(errorData.error || `Server-Fehler ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const offerNumber = quoteHeader.angebot_nr?.trim() || `Angebot-${Date.now()}`;
      link.download = `Urkalkulation-${offerNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setExportSuccess(true);
      toast({
        title: 'Urkalkulation exportiert',
        description: `"${link.download}" wurde heruntergeladen.`,
        variant: 'success',
      });
      setTimeout(() => setExportSuccess(false), 3000);
    } catch (error) {
      console.error('Urkalkulation export failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler';
      toast({
        title: 'Urkalkulation fehlgeschlagen',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
      onRunningChange?.(false);
    }
  };

  const disabled = busy || isExporting || quoteItems.length === 0;

  if (asMenuItem) {
    return (
      <DropdownMenuItem
        onSelect={() => {
          // KEIN preventDefault: einmalige Aktion, kein Dialog-Trigger — das
          // Menü darf nach der Auswahl normal schließen.
          handleExport();
        }}
        disabled={disabled}
        data-testid="button-export-urkalkulation"
      >
        {isExporting ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : exportSuccess ? (
          <Check className="mr-2 h-4 w-4" />
        ) : (
          <Calculator className="mr-2 h-4 w-4" />
        )}
        Urkalkulation (PDF)
      </DropdownMenuItem>
    );
  }

  return (
    <Button
      variant="outline"
      onClick={handleExport}
      disabled={disabled}
      className="w-full text-xs border-blue-200 text-blue-700 hover:bg-blue-50"
      data-testid="button-export-urkalkulation"
    >
      {isExporting ? (
        <>
          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          Urkalkulation wird erstellt...
        </>
      ) : exportSuccess ? (
        <>
          <Check className="h-3 w-3 mr-1" />
          Urkalkulation bereit
        </>
      ) : (
        <>
          <Calculator className="h-3 w-3 mr-1" />
          Urkalkulation (PDF)
        </>
      )}
    </Button>
  );
}
